/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/naming-convention */

// Split out of kHTML.ts 2026-10-19 (owner: smaller units for the reuse reviewer). kHTML.ts
// re-exports kHTMLTable, so `import { kHTMLTable } from "./kHTML"` keeps working.
import ks from "../ReUsableTS/kBrowserShared";
import { kStyles } from "./kStyles";
import { kHTMLTableCore } from "../kReusableCoreTS/kHTMLCore";
import { kHTMLRow } from "./kHTMLRow";

// Extends the server-safe kHTMLTableCore (kReusableCoreTS): the CSS
// constants, the header/body/footer string model, ToHtmlString() and the
// HTML/attr escaping now live in the base so the Azure Function HTML→PDF
// pipeline reuses them without a DOM. This browser subclass keeps the
// live-DOM rendering (document.createElement) on top. Borders / Visible /
// GetStyle are inherited — do not re-declare them here.
export class kHTMLTable extends kHTMLTableCore {
    //https://code-boxx.com/editable-html-table/#sec-extra
    //https://codepen.io/alexerlandsson/pen/mPWgpO
    public Table: HTMLTableElement = document.createElement("table");
    public Header: HTMLTableSectionElement | null = null;
    public Footer: HTMLTableSectionElement | null = null;
    public Body: HTMLTableSectionElement | null = null;
    public Rows: kHTMLRow[] = [];
    public constructor(ID?: string) {
        super(ID);
        this.Table = document.createElement("table");
        if (ks.KNotNullOrUndefined(ID)) {
            this.Table.id = ks.Nz(ID);
        }
        this.StartOver();
    }
    public StartOver = (): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            this.Table.innerHTML = "";
            this.Header = document.createElement("thead");
            this.Table.appendChild(this.Header);
            this.Body = document.createElement("tbody");
            this.Table.appendChild(this.Body);
            this.Footer = document.createElement("tfoot");
            this.Table.appendChild(this.Footer);
            this.Rows = [];
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHTMLTable.StartOver");
        }
    };
    public GetRows = (): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            let i = 0;
            let CurrRow: HTMLTableRowElement | null = null;
            let RowObj: kHTMLRow | null = null;
            this.Rows = [];
            if (null !== this.Table) {
                if (this.Table.tBodies.length === 1) {
                    this.Body = this.Table.tBodies.item(0);
                    if (null !== this.Body) {
                        const RowElements: HTMLCollectionOf<Element> =
                            this.Body.rows;
                        for (i = 0; i < RowElements.length; i++) {
                            CurrRow = RowElements.item(
                                i
                            ) as HTMLTableRowElement;
                            if (CurrRow.getElementsByTagName("td").length > 0) {
                                RowObj = new kHTMLRow();
                                RowObj.Row = CurrRow;
                                RowObj.GetCells();
                                this.Rows.push(RowObj);
                            }
                        }
                    }
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHTMLTable.GetRows");
        }
    };
    public AddBorders = (): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            this.AddStyles();
            this.Table.classList.add("kBlackTable");
            this.Borders = true;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHTMLTable.AddBorders");
        }
    };
    public AddRoundedBorders = (): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            if (null !== this.Table) {
                this.Table.style.border = "thin solid grey";
                this.Table.style.borderRadius = "5px";
                this.Table.style.margin = "5px";
                this.Table.style.borderCollapse = "AddRoundedBorders";
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHTMLTable.AddRoundedBorders");
        }
    };
    public AddRowObject = (
        ExistingRow: HTMLTableRowElement
    ): kHTMLRow | null => {
        if (ks.KErrorMode === true) {
            return null;
        }
        try {
            const NewRow: kHTMLRow = new kHTMLRow();
            NewRow.Row = ExistingRow;
            if (null !== this.Body) {
                this.Body.appendChild(NewRow.Row);
                this.Rows.push(NewRow);
                return NewRow;
            }
            return null;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHTMLTable.AddRowObject");
            return null;
        }
    };
    public AddRow = (Content: string): kHTMLRow => {
        if (ks.KErrorMode === true) {
            return new kHTMLRow();
        }
        try {
            const NewRow: kHTMLRow = new kHTMLRow();
            const NewCell: HTMLTableCellElement | null =
                NewRow.AddCell(Content);
            if (null !== NewCell) {
                NewRow.Cells.push(NewCell);
                if (null !== this.Body) {
                    if (null !== NewRow.Row) {
                        this.Body.appendChild(NewRow.Row);
                    }
                    this.Rows.push(NewRow);
                    return NewRow;
                }
            }
            return new kHTMLRow();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHTMLTable.AddRow");
            return new kHTMLRow();
        }
    };
    /**
     * Build the header row.
     *
     * `ClassNames` (added 2026-08-21) puts a CSS class on the `th` at the matching index, so a
     * consumer's own stylesheet owns the header's look instead of this method's inline styles.
     * It is OPTIONAL and defaults to nothing, so every existing caller is byte-identical.
     *
     * WHY IT WAS NEEDED. `Alignments` writes `style.textAlign` INLINE, and an inline style beats
     * any class a consumer adds afterwards. A design system that expresses "this is a figure
     * column" as one class - right-aligned, tabular numerals, no wrapping - therefore could not
     * be applied to a header at all: the caller had to reach into `Header.rows[0].cells` and
     * re-style each cell from outside, which is the shape of a workaround rather than a fit.
     * Passing the class in lets the class carry the alignment too, so nothing inline has to be
     * fought. Where BOTH are given, the inline `Alignments` value still wins - that is the
     * existing behaviour and it is not changed here.
     *
     * Shorter arrays are fine: an index with no entry simply gets no class, exactly as an index
     * with no width gets no width today.
     */
    public AddHeaders = (
        HeaderTitles: string[],
        MinimumWidths?: number[],
        Alignments?: string[],
        ClassNames?: string[]
    ): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            let CurrHeaderCellElement: HTMLTableCellElement | null = null;
            const HeaderRow: HTMLTableRowElement = document.createElement("tr");
            let CurrTitle = "";
            let i = 0;

            for (CurrTitle of HeaderTitles) {
                CurrHeaderCellElement = document.createElement("th");
                CurrHeaderCellElement.textContent = CurrTitle;
                CurrHeaderCellElement.style.textAlign = "left";
                if (undefined !== Alignments) {
                    if (Alignments.length > i) {
                        CurrHeaderCellElement.style.textAlign = ks.Nz(
                            Alignments[i]
                        );
                    }
                }
                if (undefined !== MinimumWidths) {
                    if (ks.KNotNullOrUndefined(MinimumWidths[i])) {
                        CurrHeaderCellElement.style.minWidth =
                            MinimumWidths[i].toString() + "px";
                    }
                }
                this._ApplyHeaderClass(CurrHeaderCellElement, ClassNames, i);
                HeaderRow.appendChild(CurrHeaderCellElement);
                i++;
            }
            if (this.Header !== null) {
                this.Header.appendChild(HeaderRow);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHTMLTable.AddHeaders");
        }
    };

    /** Put the caller's class on one header cell, where there is one for this index. Extracted so
     *  AddHeaders keeps its shape; the absence of a class is an ordinary, expected outcome. */
    private _ApplyHeaderClass = (
        HeaderCell: HTMLTableCellElement,
        ClassNames: string[] | undefined,
        Index: number
    ): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            if (undefined === ClassNames) { return; }
            if (ClassNames.length <= Index) { return; }
            const CurrClass: string = ks.Nz(ClassNames[Index]);
            if (ks.KNotEmptyString(CurrClass) === false) { return; }
            HeaderCell.className = CurrClass;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHTMLTable._ApplyHeaderClass");
        }
    };
     public MergeSingleHeader = (Columns:number): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            if(this.Table.tHead){
                const HeaderRow:HTMLTableRowElement|null = this.Table.tHead.rows.item(0);
                if(HeaderRow){
                    const HeaderCell:HTMLTableCellElement|null = HeaderRow.childNodes.item(0) as HTMLTableCellElement;
                    if(HeaderCell){
                        HeaderCell.colSpan = Columns;
                        HeaderCell.style.textAlign = "center";
                    }
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHTMLTable.MergeSingleHeader");
        }
    };   
    public AddTotalFooter = (
        Caption: string,
        FirstColumnColumns = 0,
        Summary: string
    ): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            const FooterRow: HTMLTableRowElement = document.createElement("tr");
            const LabelCellElement: HTMLTableCellElement =
                document.createElement("th");
            LabelCellElement.textContent = Caption;
            LabelCellElement.colSpan = FirstColumnColumns;
            LabelCellElement.style.textAlign = "left";
            FooterRow.appendChild(LabelCellElement);

            const SummaryCellElement: HTMLTableCellElement =
                document.createElement("th");
            SummaryCellElement.style.textAlign = "right";
            SummaryCellElement.textContent = Summary;
            FooterRow.appendChild(SummaryCellElement);
            if (this.Footer !== null) {
                this.Footer.appendChild(FooterRow);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHTMLTable.AddTotalFooter");
        }
    };

    public AddEmptyRow = (ID?: string): kHTMLRow => {
        if (ks.KErrorMode === true) {
            return new kHTMLRow();
        }
        try {
            const NewRow: kHTMLRow = new kHTMLRow();
            if (null !== this.Body) {
                if (null !== NewRow.Row) {
                    this.Body.appendChild(NewRow.Row);
                    if (ks.KNotNullOrUndefined(ID)) {
                        NewRow.Row.id = ks.Nz(ID);
                    }
                }
            }
            return NewRow;
        } catch (e) {
            //debugger;
            ks.KGlobalErrorHandler(e, "kHTMLTable.AddEmptyRow");
            return new kHTMLRow();
        }
    };

    public AddRowFirstWithElement = (Elem: HTMLElement): kHTMLRow | null => {
        if (ks.KErrorMode === true) {
            return null;
        }
        try {
            const NewRow: kHTMLRow = new kHTMLRow();
            if (this.Body) {
                NewRow.Row = this.Body.insertRow(0);
                NewRow.AddCellWithElement(Elem);
                return NewRow;
            } else {
                return null;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHTMLTable.AddRowFirstWithElement");
            return null;
        }
    };
    public AddRowWithElement = (Elem: HTMLElement): kHTMLRow => {
        if (ks.KErrorMode === true) {
            return new kHTMLRow();
        }
        try {
            const NewRow: kHTMLRow = new kHTMLRow();
            NewRow.AddCellWithElement(Elem);
            if (null !== this.Body) {
                if (null !== NewRow.Row) {
                    this.Body.appendChild(NewRow.Row);
                }
            }
            return NewRow;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHTMLTable.AddRowWithElement");
            return new kHTMLRow();
        }
    };
    public GetTableParts = (): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            if (null !== this.Table) {
                const Headers: any = this.Table.getElementsByTagName("thead");
                if (Headers.length > 0) {
                    this.Header = Headers.item(0) as HTMLTableSectionElement;
                }
                const Bodies: any = this.Table.getElementsByTagName("tbody");
                if (Bodies.length > 0) {
                    this.Body = Bodies.item(0) as HTMLTableSectionElement;
                }
                const Footers: any = this.Table.getElementsByTagName("tfoot");
                if (Footers.length > 0) {
                    this.Footer = Footers.item(0) as HTMLTableSectionElement;
                }
                this.GetRows();
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHTMLTable.GetTableParts");
        }
    };
    public FromString = (HTML: string): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            if (null !== this.Table) {
                this.Table.innerHTML = HTML;
                this.GetTableParts();
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHTMLTable.FromString");
        }
    };
    public Toggle = (): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            if (this.Visible === true) {
                this.Hide();
            } else {
                this.Show();
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHTMLTable.Toggle");
        }
    };
    public Show = (): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            if (null !== this.Table) {
                this.Table.style.display = "inline";
                this.Visible = true;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHTMLTable.Show");
        }
    };
    public Hide = (): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            if (null !== this.Table) {
                this.Table.style.display = "none";
                this.Visible = false;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHTMLTable.Hide");
        }
    };

    private static StylesInjected: boolean = false;

    public AddStyles = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (kHTMLTable.StylesInjected) { return; }
            // Opt-in strict-CSP path (kStyles.UseAdoptedSheets): a constructed sheet instead of a <style> element.
            if (kStyles.UseAdoptedSheets === true) {
                kStyles.AddStyles("kHTMLTableStyles", kHTMLTableCore.GetStyle());
                kHTMLTable.StylesInjected = true;
                return;
            }
            const Style: HTMLStyleElement = document.createElement("style");
            Style.id = "kHTMLTableStyles";
            Style.innerHTML = kHTMLTableCore.GetStyle();
            document.head.appendChild(Style);
            kHTMLTable.StylesInjected = true;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHTMLTable.AddStyles");
            return;
        }
    }
    public static GetStyle = (): string => {
        if (ks.KErrorMode === true) { return ""; }
        try {
            return `

                .kBlackTable{
                    width: 100%;
                    text-align: left;
                    border-collapse: collapse;
                }
                .kBlackTable td, .kBlackTable th {
                    border: 1px solid #000000;
                    padding: 5px 4px;
                }
                .kBlackTable thead:first-child {
                    background:#038387;
                    border-bottom: 3px solid #000000;
                    color: #FFFFFF;
                }
                .kBlackTable thead th {
                    font-weight: bold;
                    color: #FFFFFF;
                    background: #038387;
                    text-align: left;
                }
                .kBlackTable tr:nth-child(even) {
                    background: lightgrey;
                }
                `
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kHTMLTable.GetStyle");
            return "";
        }
    }
}
